/**
 * Drawer — the vendored RIGHT-SIDE dialog composition (owner ruling N-54, 2026-09-17).
 *
 * ═════════════════════════════════════════════════
 * WHY THIS FILE EXISTS AT ALL. WT5 (2026-08-13, `93377af1`) killed the inquiry
 * slide-over on the owner's ruling pair "forms as full pages + the slide-over
 * killed entirely", and no side panel has existed in the app since. N-54 orders a
 * side panel back for ONE screen — "Row → right-side drawer holding every edit
 * the row card has today" on the Products page — so this component is a
 * page-scoped instrument authorised by that later ruling, not a reversal of WT5:
 * the inquiry forms stay full pages.
 *
 * IT IS `Modal` WITH ANOTHER GEOMETRY, AND NOTHING ELSE. Same Radix Dialog
 * composition (focus trap, Escape handler, `aria-hidden` on the rest of the
 * page, scroll lock), same `bg-ink/40` scrim, same `busy` refusal of dismissal
 * for `ConfirmDialog`'s recorded reason, same title-as-accessible-name. The
 * geometry is the dead slide-over's, re-measured against the vendored rules:
 * a full-height panel on the right edge, `border-l border-line`, the authored
 * `--crf-shadow-overlay` instead of the literal rgba the original carried, and
 * `text-fg-secondary` where the original wore `text-fg-muted` (rule 5 of
 * `vendored-source.test.ts`). Width defaults to the create panel's 480px
 * (Figma `Overlay/SlideOver` 12:6410 was 440; the wider value is the one whose
 * form fields do not wrap) and never exceeds the viewport.
 *
 * ONE DIALOG AT A TIME is the CALLER's rule, as it is for `Modal`: a confirm
 * that must stack on a drawer replaces it instead (Products' rename confirm),
 * because two open dialogs are two focus traps.
 */

import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface DrawerProps {
  open: boolean;
  /** The task, naming its target — "Edit TP-SILVER". The accessible name. */
  title: string;
  /** One line under the title; optional. */
  description?: ReactNode;
  /** Refuses Escape/overlay/close-button dismissal while a request is in flight. */
  busy?: boolean;
  onClose: () => void;
  /** Action row pinned under the scrolling body. */
  footer?: ReactNode;
  children?: ReactNode;
  /** Widens or narrows the panel; the default is 480px, clamped to the viewport. */
  className?: string;
}

export function Drawer({
  open,
  title,
  description,
  busy = false,
  onClose,
  footer,
  children,
  className,
}: DrawerProps): React.JSX.Element {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        // Escape and outside pointer-downs both arrive here; `busy` refuses both.
        if (!next && !busy) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40" />
        <Dialog.Content
          {...(description === undefined ? { 'aria-describedby': undefined } : {})}
          className={cn(
            'fixed inset-y-0 right-0 z-50 flex w-[min(480px,100vw)] flex-col',
            'border-l border-line bg-surface shadow-[var(--crf-shadow-overlay)]',
            className,
          )}
        >
          {/* Plain divs for the title row and the action row, as Modal has them: a <header>
            * or <footer> inside a dialog maps to a banner/contentinfo landmark. */}
          <div className="flex items-start justify-between gap-3 border-b border-line py-4 pl-6 pr-4">
            <div className="min-w-0">
              <Dialog.Title className="text-h3 text-fg">{title}</Dialog.Title>
              {description === undefined ? null : (
                <Dialog.Description className="mt-1 text-label text-fg-secondary">
                  {description}
                </Dialog.Description>
              )}
            </div>
            {/* The one dismiss control, named for what it closes. `busy` makes it
              * inert the same way it refuses Escape — the request in flight is not
              * something a click should hide. */}
            <Dialog.Close
              aria-label="Close"
              disabled={busy}
              className="flex size-8 shrink-0 items-center justify-center rounded-sm text-fg-secondary hover:bg-subtle hover:text-fg"
            >
              <X className="size-4" aria-hidden="true" />
            </Dialog.Close>
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
            {children}
          </div>
          {footer === undefined ? null : (
            <div className="flex items-center justify-end gap-2 border-t border-line px-6 py-3.5">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
